import { emailSchedulePartsToUtcMilli, isValidEmailScheduleDate, outboundStatusLabel } from "./emailBackendMappers";
import React, { useMemo, useRef, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import CalendarDateInput from "../../common/dashboard/CalendarDateInput";
import TimeField from "../../common/dashboard/TimeField";
import { validateEmailSchedule } from "./emailManageActions";
import { ConfirmDialog } from "../../common/dashboard/ConfirmDialog";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../utils/userFacingError";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";
import {
  EmailFooterProfile,
  renderEmailBuilderState,
} from "./emailBuilderData";
import { EmailPreviewDialog, EmailPreviewFrame } from "./EmailPreview";
import { EmailFormState, RecipientPreviewSummary, getEmailTypeLabel } from "./emailMockData";
import {
  confirmPreparedNewsletterTestSend,
  NewsletterTestSendPreview,
  PendingNewsletterTestSend,
  prepareNewsletterTestSendConfirmation,
} from "./emailTestSend";
import {
  AudienceSummaryRow,
  buildRecipientSourceRows,
  EmailFormMode,
  Field,
  UpdateEmailField,
  attachmentAccept,
  formatNumber,
  inputClassName,
  darkButtonClassName,
  primaryButtonClassName,
  sectionClassName,
  stepDescriptionClassName,
  successButtonClassName,
  textareaClassName,
} from "./emailWizardShared";

export type StepFourReviewProps = {
  mode: EmailFormMode;
  email: EmailFormState;
  isBusy: boolean;
  isSendNowLoading: boolean;
  isSentRecord: boolean;
  canRunSavedBackendAction: boolean;
  intendedRecipientCount: number | null;
  directRecipientCount: number;
  audienceRows: AudienceSummaryRow[];
  recipientPreview?: RecipientPreviewSummary | null;
  isAudiencePreviewLoading?: boolean;
  audiencePreviewError?: string;
  goToCampaignDetails: () => void;
  updateEmailField: UpdateEmailField;
  uploadImage: (file: File) => Promise<string>;
  addAttachment: (file?: File) => Promise<void>;
  onDuplicateAsDraft?: (email: EmailFormState) => Promise<void>;
  previewTestSend: (
    recipientEmails: string[],
    clientRequestId: string
  ) => Promise<NewsletterTestSendPreview>;
  sendReview: (
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => Promise<void>;
  scheduleEmail: () => Promise<void>;
  cancelSchedule: () => Promise<void>;
  publishNewsroom: () => Promise<void>;
  unpublishNewsroom: () => Promise<void>;
  sendNow: () => Promise<void>;
  hasSendReviewAction: boolean;
  hasScheduleAction: boolean;
  hasCancelScheduleAction: boolean;
  hasPublishNewsroomAction: boolean;
  hasUnpublishNewsroomAction: boolean;
  hasSendNowAction: boolean;
  footerProfile?: EmailFooterProfile | null;
};

const cardHeadingClassName =
  "text-[18px] font-semibold leading-tight text-[#0A0A0A]";
const subCardClassName =
  "rounded-xl border border-[#ECECEC] bg-white px-5 py-[22px] sm:px-6";
const subCardHeadingClassName = "text-[16px] font-semibold text-[#0A0A0A]";
const outlineActionClassName =
  "inline-flex min-h-[43px] items-center justify-center gap-2 rounded-lg border border-[#C7C7C7] bg-white px-5 py-2.5 text-[14px] font-semibold text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:cursor-not-allowed disabled:opacity-60";

const newsroomZone = (timeZone?: string | null) => normalizeCmpTimeZone(timeZone || "").timeZone;
export const formatNewsroomDate = (utcMilli: number, timeZone?: string | null): string => {
  try {
    return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: newsroomZone(timeZone) }).format(utcMilli);
  } catch {
    return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(utcMilli);
  }
};
/** "October 12, 2026 at 5:01 PM" in the chamber time zone (design 16 newsroom stamp). */
export const formatNewsroomStamp = (utcMilli: number, timeZone?: string | null): string => {
  let time: string;
  try {
    time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: newsroomZone(timeZone) }).format(utcMilli);
  } catch {
    time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(utcMilli);
  }
  return `${formatNewsroomDate(utcMilli, timeZone)} at ${time}`;
};

const StepFourReview = ({
  mode,
  email,
  isBusy,
  isSendNowLoading,
  isSentRecord,
  canRunSavedBackendAction,
  intendedRecipientCount,
  directRecipientCount,
  audienceRows,
  recipientPreview,
  isAudiencePreviewLoading = false,
  audiencePreviewError = "",
  goToCampaignDetails,
  updateEmailField,
  uploadImage,
  addAttachment,
  previewTestSend,
  sendReview,
  scheduleEmail,
  cancelSchedule,
  publishNewsroom,
  unpublishNewsroom,
  sendNow,
  hasSendReviewAction,
  hasScheduleAction,
  hasCancelScheduleAction,
  hasPublishNewsroomAction,
  hasUnpublishNewsroomAction,
  hasSendNowAction,
  footerProfile,
}: StepFourReviewProps) => {
  const noun = useCmpOrganizationNoun();
  const newsroomImageInputRef = useRef<HTMLInputElement | null>(null);
  const [newsroomImageUploading, setNewsroomImageUploading] = useState(false);
  const [newsroomImageError, setNewsroomImageError] = useState("");
  const newsroomTitle = email.seoTitle.trim() || email.subject.trim() || email.campaignName.trim() || "Email Subject Line";
  const newsroomDescription = email.seoDescription.trim() || email.preheader.trim() || email.campaignDescription.trim();
  // Design (16) Newsroom Preview: the image is saved with the email (Update
  // or Publish to Newsroom saves it); JPG or PNG only.
  const uploadNewsroomImage = async (file: File) => {
    setNewsroomImageError("");
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setNewsroomImageError("Upload a JPG or PNG image.");
      return;
    }
    setNewsroomImageUploading(true);
    try {
      updateEmailField("newsroomImageUrl", await uploadImage(file));
    } catch (error) {
      setNewsroomImageError(getUserFacingErrorText(error, buildStaffErrorFallback("upload the image", "Check your internet connection.")));
    } finally {
      setNewsroomImageUploading(false);
    }
  };
  const hasCurrentPreviewTotal = !isSentRecord && !isAudiencePreviewLoading && !audiencePreviewError &&
    !!recipientPreview && Number.isInteger(recipientPreview.uniqueValidRecipientCount) && recipientPreview.uniqueValidRecipientCount >= 0;
  const resolvedRecipientCount = isSentRecord ? intendedRecipientCount
    : hasCurrentPreviewTotal ? recipientPreview.uniqueValidRecipientCount : null;
  const recipientSourceRows = useMemo(() => hasCurrentPreviewTotal && recipientPreview
    ? buildRecipientSourceRows(recipientPreview) : null, [hasCurrentPreviewTotal, recipientPreview]);
  const selectedAudienceLabels = [
    ...audienceRows.map((row) => row.label),
    ...(directRecipientCount > 0 ? ["Additional Recipients"] : []),
  ];
  const intendedRecipientCountLabel =
    resolvedRecipientCount === null
      ? "—"
      : formatNumber(resolvedRecipientCount);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [testEmailInput, setTestEmailInput] = useState("");
  const [testEmailError, setTestEmailError] = useState("");
  const [pendingTestSend, setPendingTestSend] =
    useState<PendingNewsletterTestSend | null>(null);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  // Shown at the date field, so it does not look like a test email problem.
  const scheduleDateError =
    email.scheduledDate && !isValidEmailScheduleDate(email.scheduledDate)
      ? "Enter a valid date or choose one from the calendar."
      : "";
  const [isPreparingTest, setIsPreparingTest] = useState(false);
  const [isConfirmingTest, setIsConfirmingTest] = useState(false);
  const testSendBusy = isBusy || isPreparingTest || isConfirmingTest;

  const handleScheduleEmail = async () => {
    const error = validateEmailSchedule(
      emailSchedulePartsToUtcMilli(email.scheduledDate, email.scheduledTime, email.timeZone)
    );
    setScheduleError(error || "");
    // Schedule and test send save the draft first, so a new email can use
    // them before its first save (D-EM6).
    if (
      error ||
      !hasScheduleAction ||
      isBusy ||
      isSentRecord
    )
      return;
    try {
      await scheduleEmail();
    } catch (error) {
      setScheduleError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "schedule this email",
            "Check the Scheduled Date and Scheduled Time."
          )
        )
      );
    }
  };

  // The preview thumbnail and the full preview must both render THIS
  // campaign's real built content so they always agree with Email Summary.
  const renderedEmail = useMemo(
    () =>
      renderEmailBuilderState(email.builderState, footerProfile || undefined),
    [email.builderState, footerProfile]
  );

  const handleSendTestEmail = async () => {
    if (testSendBusy || !hasSendReviewAction) return;
    setIsPreparingTest(true);
    try {
      setTestEmailError("");
      setPendingTestSend(
        await prepareNewsletterTestSendConfirmation({
          rawRecipientInput: testEmailInput,
          previewTestSend,
        })
      );
    } catch (error) {
      setTestEmailError(
        applyCmpOrganizationNoun(
          getUserFacingErrorText(
            error,
            buildStaffErrorFallback(
              "send the test email",
              "Check the email addresses."
            )
          ),
          noun
        )
      );
    } finally {
      setIsPreparingTest(false);
    }
  };

  const confirmSendTestEmail = async () => {
    if (!pendingTestSend || testSendBusy) return;
    setIsConfirmingTest(true);
    setTestEmailError("");
    const attempt = await confirmPreparedNewsletterTestSend({
      pending: pendingTestSend,
      sendTestEmail: sendReview,
    });
    if (attempt.sent) {
      setPendingTestSend(null);
    } else {
      setPendingTestSend(attempt.pending);
      setTestEmailError(attempt.error);
    }
    setIsConfirmingTest(false);
  };

  const summaryRows = [
    { label: "Email Name", value: email.campaignName || "Not set" },
    { label: "Subject Line", value: email.subject || "Not set" },
    { label: "Sender Name", value: email.fromName || "Not set" },
    {
      label: "Reply-To Email",
      value: email.replyToEmail || email.fromEmail || "Not set",
    },
    {
      label: "Email Type",
      value:
        email.templateId || email.id
          ? getEmailTypeLabel(email.emailType).toUpperCase()
          : "NOT SELECTED",
    },
    { label: "Content Type", value: "HTML Email" },
  ];

  return (
    <div className="space-y-[22px]">
      <div>
        <h2 className="text-[18px] font-semibold text-[#0A0A0A]">
          Review and Send
        </h2>
        <p className={stepDescriptionClassName}>
          Review your email campaign details before sending to your audience.
        </p>
      </div>
      <section className={sectionClassName} data-email-review-section="summary">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h3 className={cardHeadingClassName}>Email Summary</h3>
          <button
            type="button"
            className={`${outlineActionClassName} !min-h-[39px] !px-4 !text-[13px] !leading-[normal]`}
            onClick={goToCampaignDetails}
            disabled={isBusy}
          >
            <EditOutlinedIcon sx={{ fontSize: 13 }} /> Edit Campaign Details
          </button>
        </div>
        <dl className="mt-[22px] space-y-4">
          {summaryRows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4"
            >
              <dt className="text-[14px] text-[#727272]">{row.label}</dt>
              <dd className="min-w-0 break-words text-[15px] font-semibold text-[#0A0A0A]">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
        <div className="mt-[22px] rounded-[10px] border border-[#ECECEC] bg-[#FAFAFA] p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <VisibilityOutlinedIcon
              className="text-[#727272]"
              sx={{ fontSize: 18 }}
            />
            <h4 className="text-[15px] font-semibold text-[#0A0A0A]">
              Email Preview
            </h4>
          </div>
          <div className="mt-5 flex justify-center">
            <EmailPreviewFrame
              html={renderedEmail.html}
              title="Email preview"
              scrolling="no"
              fitToContainer
              containerClassName="h-[380px] w-full max-w-[520px] overflow-hidden rounded-lg border border-[#ECECEC] bg-white shadow-sm"
              iframeClassName="pointer-events-none h-[900px] w-full border-0"
            />
          </div>
          <div className="mt-4 text-center">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#0A0A0A] hover:underline"
              onClick={() => setFullPreviewOpen(true)}
            >
              Preview Full Email <OpenInNewIcon sx={{ fontSize: 15 }} />
            </button>
            <p className="mt-2 text-[12px] text-[#9E9E9E]">
              See how your email will look to recipients.
            </p>
          </div>
        </div>
      </section>

      <section
        className={sectionClassName}
        data-email-review-section="audience"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h3 className={cardHeadingClassName}>Audience Summary</h3>
          <div className="flex items-center gap-2 rounded-lg bg-[#F7F7F8] px-[14px] py-[9px]">
            <span className="text-[12px] font-semibold tracking-[0.05em] text-[#727272]">
              TOTAL RECIPIENTS
            </span>
            <span className="text-[17px] font-semibold text-[#0A0A0A]">
              {intendedRecipientCountLabel}
            </span>
          </div>
        </div>
        <p className="mt-2 text-[14px] text-[#0A0A0A]">
          Your email will be sent to the following audience.
        </p>
        <p className="mt-[22px] text-[12px] font-semibold uppercase tracking-[0.05em] text-[#727272]">
          Selected Audience
        </p>
        <div className="mt-3 text-[14px]" aria-label="Selected audience context">
          {selectedAudienceLabels.length === 0 ? (
            <p className="rounded-lg border border-dashed border-[#C9C9C9] p-4 text-[14px] text-[#727272]">
              No audience selected yet — go back to Step 1 to choose who
              receives this email.
            </p>
          ) : (
            <p className="break-words">{selectedAudienceLabels.join("; ")}</p>
          )}
        </div>
        <div className="mt-[22px]" aria-label="Recipients by source">
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[#727272]">Recipients by Source</p>
          {recipientSourceRows === null ? (
            <p className="mt-3 text-[14px] text-[#727272]" role={audiencePreviewError ? "alert" : "status"}>
              {isSentRecord ? "Recipient source counts are not available for this saved email."
                : audiencePreviewError || (hasCurrentPreviewTotal ? "We could not show the recipient count for every source."
                  : "We are still counting your recipients.")}
            </p>
          ) : recipientSourceRows.length === 0 ? (
            <p className="mt-3 text-[14px] text-[#727272]">No one in this audience can receive this email. Choose another audience.</p>
          ) : (
            <dl className="mt-3 space-y-3">
              {recipientSourceRows.map((row) => (
                <div key={row.id} className="flex items-center justify-between gap-4 rounded-lg border border-[#ECECEC] px-4 py-[14px] text-[14px] font-semibold text-[#0A0A0A]">
                  <dt className="min-w-0 break-words">{row.label}</dt>
                  <dd className="shrink-0">{formatNumber(row.count)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
        <div className="mt-5 flex items-start gap-3 rounded-[10px] border border-[#ECECEC] bg-[#FAFAFA] px-4 py-[14px]">
          <InfoOutlinedIcon
            className="mt-0.5 text-[#727272]"
            fontSize="small"
          />
          <div className="text-[14px]">
            <p className="font-semibold text-[#0A0A0A]">
              {resolvedRecipientCount === null
                ? audiencePreviewError ? "We could not count your recipients. Refresh the page to try again." : "We are still counting your recipients."
                : `This email will be sent to ${intendedRecipientCountLabel} unique recipients.`}
            </p>
            <p className="mt-1 text-[#727272]">
              {resolvedRecipientCount === null
                ? "The recipient total shows here when the count is ready."
                : "Duplicate email addresses have been removed."}
            </p>
          </div>
        </div>

        <div className="mt-[22px] space-y-[22px]">
          <section
            className={subCardClassName}
            data-email-review-section="attachments"
          >
            <h3 className={subCardHeadingClassName}>Attachments</h3>
            <p className="mt-2 text-[14px] leading-5 text-[#727272]">
              Images are converted and optimized automatically. Keep combined
              attachments at 6MB or less so the email can be sent reliably.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={attachmentAccept}
              className="hidden"
              disabled={isBusy || isSentRecord}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                void addAttachment(file);
              }}
            />
            <button
              type="button"
              className={`${outlineActionClassName} mt-5`}
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy || isSentRecord}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
              Upload Attachment
            </button>
            <div className="mt-[14px] grid grid-cols-1 gap-2">
              {email.attachments.length === 0 ? (
                <p className="rounded-lg border-[1.5px] border-dashed border-[#C9C9C9] px-5 py-7 text-[14px] text-[#9E9E9E]">
                  No attachments added.
                </p>
              ) : (
                email.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#ECECEC] px-[14px] py-[11px]"
                  >
                    <DescriptionOutlinedIcon
                      className="shrink-0 text-[#727272]"
                      sx={{ fontSize: 20 }}
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        title={attachment.name}
                        className="truncate text-[14px] font-semibold text-[#0A0A0A]"
                      >
                        {attachment.name}
                      </p>
                    </div>
                    <span className="shrink-0 text-[14px] text-[#727272]">
                      {attachment.sizeLabel}
                    </span>
                    <button
                      type="button"
                      title="Remove attachment"
                      aria-label={`Remove attachment ${attachment.name}`}
                      className="shrink-0 text-[#F21B1B] disabled:opacity-60"
                      disabled={isBusy || isSentRecord}
                      onClick={() =>
                        updateEmailField(
                          "attachments",
                          email.attachments.filter(
                            (item) => item.id !== attachment.id
                          )
                        )
                      }
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>

          {mode === "update" ? (
            <section
              className={subCardClassName}
              data-email-review-section="tracking"
            >
              <h3 className={subCardHeadingClassName}>Delivery Tracking</h3>
              <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-[14px]">
                {[
                  { label: "Sent", value: email.sentRecipientCount },
                  { label: "Failed", value: email.failedRecipientCount },
                  { label: "Opens", value: email.openCount },
                  { label: "Clicks", value: email.clickCount },
                ].map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-[10px] border border-[#ECECEC] bg-white px-[18px] py-4"
                  >
                    <p className="text-[14px] font-semibold text-[#727272]">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[18px] font-semibold text-[#0A0A0A]">
                      {formatNumber(metric.value)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-5 overflow-x-auto rounded-lg border border-[#ECECEC]">
                <table className="min-w-[820px] w-full text-left text-[13px]">
                  <thead className="bg-[#FAFAFA]">
                    <tr>
                      {[
                        "Kind",
                        "Status",
                        "Recipient",
                        "Intended Recipient",
                        "Created",
                        "Error",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="border-b border-[#ECECEC] px-3 py-[14px] font-semibold text-[#727272]"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {email.outboundEmails.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-5 text-[#727272]">
                          No outbound delivery rows yet.
                        </td>
                      </tr>
                    ) : (
                      email.outboundEmails.slice(0, 8).map((outboundEmail) => (
                        <tr
                          key={outboundEmail.id}
                          className="border-b border-[#ECECEC]"
                        >
                          <td className="px-3 py-4 font-semibold">
                            {outboundEmail.kind}
                          </td>
                          <td
                            className={`px-3 py-4 font-semibold ${outboundEmail.status === "sent" ? "text-[#10B981]" : outboundEmail.status === "failed" ? "text-[#F21B1B]" : "text-[#727272]"}`}
                          >
                            {outboundStatusLabel(outboundEmail.status)}
                          </td>
                          <td className="px-3 py-4">{outboundEmail.toEmail}</td>
                          <td className="px-3 py-4">
                            {outboundEmail.intendedToEmail || "-"}
                          </td>
                          <td className="px-3 py-4">
                            {outboundEmail.createdAt}
                          </td>
                          <td className="px-3 py-4 text-[#F21B1B]">
                            {outboundEmail.errorMessage}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section
            className={`${subCardClassName} !border-[1.5px] !border-cpaGold`}
            data-email-review-section="test-send"
          >
            <h3 className={subCardHeadingClassName}>Send Test Email</h3>
            <p className="mt-2 text-[14px] text-[#727272]">
              Send a test email to yourself or a colleague to review before
              sending to all recipients. You will confirm the exact recipients
              and sender before anything is sent.
            </p>
            <label className="mt-5 block">
              <span className="text-[14px] font-semibold text-[#0A0A0A]">
                Send test email to
              </span>
              <input
                value={testEmailInput}
                onChange={(event) => {
                  setTestEmailInput(event.target.value);
                  setTestEmailError("");
                }}
                className={`${inputClassName} mt-2`}
                placeholder="Enter email address"
                disabled={testSendBusy}
              />
            </label>
            {testEmailError ? (
              <p role="alert" className="mt-2 text-[14px] text-[#F21B1B]">
                {testEmailError}
              </p>
            ) : null}
            <p className="mt-2 text-[12px] text-[#9E9E9E]">
              Separate multiple emails with a comma
            </p>
            <button
              type="button"
              className={`${primaryButtonClassName} mt-5`}
              onClick={handleSendTestEmail}
              disabled={!hasSendReviewAction || testSendBusy}
            >
              {isPreparingTest ? (
                <CircularProgress color="inherit" size={16} />
              ) : null}
              Send Test Email
            </button>
            {!hasSendReviewAction ? (
              <p className="mt-3 text-[14px] text-[#727272]">
                Save the draft first, then send the test email from the saved
                email.
              </p>
            ) : null}
          </section>

          {/* Claude Design (19) CMP Dashboard.dc.html:4894-4911 email Step 4 schedule card. */}
          <section
            className={`${subCardClassName} !border-[1.5px] !border-[#C7C7C7]`}
            data-email-review-section="schedule"
          >
            <h3 className={subCardHeadingClassName}>Schedule (Optional)</h3>
            <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
              Choose a date and time if you want to schedule this email instead
              of sending it right away.
            </p>
            {/* Design (20) D:4948-4951 (EM-4-1): title and one sentence, no
                time zone line. The scheduled time is still read in the chamber
                time zone. */}
            <div className="mt-[14px] grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div>
                <label
                  htmlFor="email-scheduled-date"
                  className="mb-2 block text-[14px] font-semibold text-[#0A0A0A]"
                >
                  Scheduled Date
                </label>
                <CalendarDateInput
                  id="email-scheduled-date"
                  mode="fullDate"
                  value={email.scheduledDate}
                  onChange={(value) => {
                    setScheduleError("");
                    updateEmailField("scheduledDate", value);
                  }}
                  ariaLabel="Scheduled Date"
                  ariaInvalid={scheduleDateError ? true : undefined}
                  ariaDescribedBy={scheduleDateError ? "email-scheduled-date-error" : undefined}
                  placeholder="Select scheduled date"
                  disabled={isBusy || isSentRecord}
                  displayFormat="long"
                  appearance="claude"
                  iconPosition="left"
                  inputClassName={`${inputClassName}${scheduleDateError ? " !border-[#F21B1B]" : ""}`}
                />
                {scheduleDateError ? (
                  <p id="email-scheduled-date-error" role="alert" className="mt-2 text-[14px] text-[#F21B1B]">
                    {scheduleDateError}
                  </p>
                ) : null}
              </div>
              <div>
                <label
                  htmlFor="email-scheduled-time"
                  className="mb-2 block text-[14px] font-semibold text-[#0A0A0A]"
                >
                  Scheduled Time
                </label>
                <TimeField
                  id="email-scheduled-time"
                  ariaLabel="Scheduled Time"
                  value={email.scheduledTime}
                  onChange={(value) => {
                    setScheduleError("");
                    updateEmailField("scheduledTime", value);
                  }}
                  placeholder="Select Time"
                  disabled={isBusy || isSentRecord}
                  className="!h-[43px]"
                />
              </div>
              <div className="lg:col-span-2">
                <Field label="Internal Review Note">
                  <textarea
                    value={email.reviewNote}
                    onChange={(event) =>
                      updateEmailField("reviewNote", event.target.value)
                    }
                    // Design (20) D:4964: note border #9E9E9E.
                    className={`${textareaClassName} !min-h-[96px] !border-[#9E9E9E]`}
                    disabled={isBusy}
                    placeholder="Add a note for review, schedule, or send history."
                  />
                </Field>
                {!!email.reviewHistory?.length && <details className="mt-3 text-sm">
                  <summary className="cursor-pointer font-semibold">Review note history</summary>
                  <ol className="mt-2 space-y-3">{email.reviewHistory.map((entry, index) => <li key={`${entry.createdAtUtcMilli}-${index}`}>
                    <p className="font-semibold">{entry.action.replace(/_/g, " ")} · {entry.actorName || entry.actorUserId} · {new Date(entry.createdAtUtcMilli).toLocaleString("en-CA", { timeZone: email.timeZone })}</p>
                    <p className="whitespace-pre-wrap">{entry.note}</p>
                  </li>)}</ol>
                </details>}
              </div>
            </div>
            {scheduleError ? (
              <p role="alert" className="mt-3 text-[14px] text-[#F21B1B]">
                {scheduleError}
              </p>
            ) : null}
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                className={darkButtonClassName}
                onClick={handleScheduleEmail}
                disabled={!hasScheduleAction || isBusy || isSentRecord}
              >
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
                Schedule to Send
              </button>
              {email.status === "scheduled" ? (
                <button
                  type="button"
                  className={outlineActionClassName}
                  onClick={cancelSchedule}
                  disabled={
                    !canRunSavedBackendAction ||
                    !hasCancelScheduleAction ||
                    isBusy
                  }
                >
                  Cancel Schedule
                </button>
              ) : null}
            </div>
          </section>

          <section
            className={subCardClassName}
            data-email-review-section="newsroom-preview"
          >
            <h3 className={subCardHeadingClassName}>Newsroom Preview</h3>
            <p className="mt-1 text-[14px] text-[#0A0A0A]">
              This is how your communication will appear in your {noun.singular}’s newsroom on your website. The type of article, the title, and the description are pulled from Campaign Details. Upload a thumbnail image to display on the newsroom card for users to easily identify the article. If left blank, your logo will appear.
            </p>
            <div className="mt-4 flex flex-wrap gap-5 rounded-xl border border-[#ECECEC] p-4">
              <input
                ref={newsroomImageInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                aria-label="Upload Preview Image file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void uploadNewsroomImage(file);
                }}
              />
              <button
                type="button"
                title="Upload Preview Image"
                disabled={isBusy || newsroomImageUploading}
                onClick={() => newsroomImageInputRef.current?.click()}
                className="flex h-[150px] flex-[0_0_180px] flex-col items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-[#9E9E9E] bg-[#F7F7F8] bg-cover bg-center disabled:opacity-60"
                style={email.newsroomImageUrl ? { backgroundImage: `url("${email.newsroomImageUrl}")` } : undefined}
              >
                {newsroomImageUploading ? <CircularProgress size={22} /> : !email.newsroomImageUrl ? (
                  <>
                    <FileUploadOutlinedIcon sx={{ fontSize: 26, color: "#727272" }} />
                    <span className="text-[13px] font-semibold text-[#0A0A0A]">Upload Preview Image</span>
                    <span className="text-[12px] text-[#727272]">JPG or PNG</span>
                  </>
                ) : <span className="sr-only">Change Preview Image</span>}
              </button>
              <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2.5 text-[13px] text-[#727272]">
                  <span>{formatNewsroomDate(email.newsroomPublishedAtUtcMilli || Date.now(), email.timeZone)}</span>
                  <span className="rounded-md bg-[#F1F1F3] px-2 py-[3px] font-semibold text-[#0A0A0A]">{getEmailTypeLabel(email.emailType)}</span>
                  <span>1 min read</span>
                </div>
                <p className="truncate text-[20px] font-bold text-[#0A0A0A]" title={newsroomTitle}>{newsroomTitle}</p>
                <p className="text-[14px] text-[#3A3A3A]">{newsroomDescription}</p>
                <div className="mt-auto flex flex-wrap items-end justify-between gap-3">
                  <span className="text-[13px] text-[#3A3A3A]">By {email.fromName || "From Name"}</span>
                  <span className="rounded-md bg-[#1E9BD7] px-[18px] py-[9px] text-[13px] font-semibold text-white">Read More</span>
                </div>
              </div>
            </div>
            {newsroomImageError ? <p role="alert" className="mt-2 text-[13px] text-[#E24C4C]">{newsroomImageError}</p> : null}
          </section>

          <section
            className={subCardClassName}
            data-email-review-section="newsroom"
          >
            <h3 className={subCardHeadingClassName}>Publish to Newsroom</h3>
            <p className="mt-1 text-[14px] text-[#0A0A0A]">
              Choose whether this email communication should also be published to your {noun.singular}’s public newsroom on your website. Only the latest saved publication will appear.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border-0 bg-[#10B981] px-5 py-[11px] text-[14px] font-semibold text-white hover:bg-[#0E9F6E] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={publishNewsroom}
                disabled={!hasPublishNewsroomAction || isBusy || email.status === "sending"}
              >
                Publish to Newsroom
              </button>
              {email.newsroomStatus === "published" ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#F21B1B] bg-white px-5 py-[10px] text-[14px] font-semibold text-[#F21B1B] hover:bg-[#FDECEC] disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={unpublishNewsroom}
                  disabled={!canRunSavedBackendAction || !hasUnpublishNewsroomAction || isBusy}
                >
                  Unpublish from Newsroom
                </button>
              ) : null}
            </div>
            {email.newsroomStatus === "published" && email.newsroomPublishedAtUtcMilli ? (
              <p className="mt-2 text-[13px] font-semibold text-[#10B981]">Published: {formatNewsroomStamp(email.newsroomPublishedAtUtcMilli, email.timeZone)}</p>
            ) : null}
            {email.newsroomStatus === "unpublished" && email.newsroomUnpublishedAtUtcMilli ? (
              <p className="mt-2 text-[13px] font-semibold text-[#F21B1B]">Unpublished: {formatNewsroomStamp(email.newsroomUnpublishedAtUtcMilli, email.timeZone)}</p>
            ) : null}
          </section>

          <section
            className={`${subCardClassName} !border-[1.5px] !border-[#10B981]`}
            data-email-review-section="send"
          >
            <h3 className={subCardHeadingClassName}>
              Approve &amp; Send to All
            </h3>
            <p className="mt-2 text-[14px] text-[#727272]">
              {resolvedRecipientCount === null
                ? "Approve and send this email to everyone in your audience."
                : `Approve and send this email campaign to all ${intendedRecipientCountLabel} recipients.`}
            </p>
            <button
              type="button"
              className={`${successButtonClassName} mt-5`}
              onClick={sendNow}
              disabled={!hasSendNowAction || isBusy || isSentRecord}
            >
              {isSendNowLoading ? (
                <>
                  <CircularProgress color="inherit" size={18} />
                  Sending Email...
                </>
              ) : (
                "Approve & Send Email"
              )}
            </button>
            {!hasSendNowAction ? (
              <p className="mt-3 text-[14px] text-[#727272]">
                You cannot send this email from this page. Open it from Manage
                Emails & Communications.
              </p>
            ) : null}
          </section>
        </div>
      </section>
      <EmailPreviewDialog
        open={fullPreviewOpen}
        email={email}
        persistedHtml={renderedEmail.html}
        loading={false}
        footerProfile={footerProfile}
        onClose={() => setFullPreviewOpen(false)}
      />
      <ConfirmDialog
        open={!!pendingTestSend}
        constrainContentWidth
        variant="confirm"
        title="Send this test email?"
        message={
          pendingTestSend ? (
            <>
              <span className="block">
                <span className="font-bold text-gray-900">To:</span>{" "}
                {pendingTestSend.preview.normalizedRecipientEmails.join(", ")}
              </span>
              <span className="mt-2 block">
                <span className="font-bold text-gray-900">From:</span>{" "}
                {pendingTestSend.preview.fromEmail}
              </span>
              <span className="mt-2 block">
                <span className="font-bold text-gray-900">Reply-To:</span>{" "}
                {pendingTestSend.preview.replyToEmail || "Not set"}
              </span>
              <span className="mt-3 block">
                This sends only to the addresses above, not to the campaign
                audience.
              </span>
              {testEmailError ? (
                <span className="mt-3 block font-semibold text-red-600">
                  {testEmailError}
                </span>
              ) : null}
            </>
          ) : undefined
        }
        confirmLabel="Send Test Email"
        cancelLabel="Go Back"
        confirmColor="gold"
        loading={testSendBusy}
        onConfirm={confirmSendTestEmail}
        onClose={() => {
          if (!testSendBusy) setPendingTestSend(null);
        }}
      />
    </div>
  );
};

export default StepFourReview;
